import classNames from 'classnames'
import { FormikProps, withFormik } from 'formik'
import React, { RefObject, useEffect, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { Form } from '@lib/formik'

import { Button } from '@ui/components/Button'
import { Input } from '@ui/components/Input'

import { Box } from '@shared/components/Box'
import { FullScreenFooter } from '@shared/templates/FullScreenLayout'

import { TwoFactorAuthEvents, TwoFactorAuthLocationState } from './useTwoFactorAuth'

import styles from './TwoFactorAuthPage.module.scss'

export interface CodeFormValues {
  code1: string
  code2: string
  code3: string
  code4: string
  code5: string
  code6: string
}

const fieldKeys: (keyof CodeFormValues)[] = ['code1', 'code2', 'code3', 'code4', 'code5', 'code6']

const TwoFactorAuthFormUI: React.FunctionComponent<FormikProps<CodeFormValues> & OuterProps> = (
  props
) => {
  const {
    openRecoveryModal,
    resendText,
    errorText,
    autoFocus,
    withConfirmButton,
    stickyConfirmButton,
    contentRef,
  } = props
  const { handleSubmit, values, validationError, isSubmitting } = props
  const { t } = useTranslation()
  const isCodeComplete = fieldKeys.every((fk) => !!values[fk])
  const location = useLocation()
  const locationState = location.state as TwoFactorAuthLocationState
  const isLoginAuthEvent =
    locationState?.twoFactorAuthData?.twoFactorAuthEvent === TwoFactorAuthEvents.LogIn ||
    location.pathname.includes('/login/2fa')

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    props.setFieldValue(name, value)
    let nextField: keyof CodeFormValues | undefined
    if (name === 'code1') {
      nextField = 'code2'
    } else if (name === 'code2') {
      nextField = 'code3'
    } else if (name === 'code3') {
      nextField = 'code4'
    } else if (name === 'code4') {
      nextField = 'code5'
    } else if (name === 'code5') {
      nextField = 'code6'
    }
    if (nextField) {
      const next = document.getElementById(nextField)
      if (next && !!value) {
        next.focus()
      }
    }
  }

  const handleStepBack = (
    e: {
      key: string
      preventDefault: () => void
    },
    name: keyof CodeFormValues,
    value: string
  ) => {
    e.preventDefault()
    let previousField: keyof CodeFormValues | undefined
    if (name === 'code2') {
      previousField = 'code1'
    } else if (name === 'code3') {
      previousField = 'code2'
    } else if (name === 'code4') {
      previousField = 'code3'
    } else if (name === 'code5') {
      previousField = 'code4'
    } else if (name === 'code6') {
      previousField = 'code5'
    }
    if (previousField) {
      const previous = document.getElementById(previousField)
      if (previous && !value) {
        props.setFieldValue(previousField, '')
        previous?.focus()
      }
    }
  }

  useEffect(() => {
    if (!withConfirmButton && isCodeComplete && !isSubmitting) {
      handleSubmit()
    }
  }, [values]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text/plain')

    const pastedValues = pastedData.match(/\d+/g)?.join('').split('') || []
    if (pastedValues.length <= fieldKeys.length) {
      pastedValues.forEach((value, index) => {
        props.setFieldValue(fieldKeys[index], value)
      })
    }
  }

  const confirmButton = (
    <Button.Primary
      type='submit'
      size='lg'
      fullWidth
      disabled={!isCodeComplete}
      loading={isSubmitting}
    >
      {t('Confirm')}
    </Button.Primary>
  )

  return (
    <Form className={classNames(styles.form, { [styles.fillHeight]: stickyConfirmButton })}>
      <Box
        flex
        direction='column'
        align='flex-start'
        className={stickyConfirmButton ? styles.fillHeight : undefined}
      >
        <Box flex className={styles.codeWrapper}>
          {fieldKeys.map((fk: keyof CodeFormValues) => (
            <Input
              type='tel'
              id={fk}
              key={fk}
              name={fk}
              onPaste={handlePaste}
              variant='digital'
              maxLength={1}
              value={values[fk as keyof CodeFormValues] ? '*' : ''}
              onChange={onChange}
              onKeyDown={(e) => {
                if (e.key === 'Backspace' && !values[fk as keyof CodeFormValues]) {
                  handleStepBack(e, fk, values[fk as keyof CodeFormValues])
                }
                if (['e', 'E', '+', '-'].includes(e.key)) {
                  return e.preventDefault()
                }
              }}
              error={!!validationError}
              autoFocus={fk === 'code1' && autoFocus}
            />
          ))}
        </Box>
        {!!validationError && <div className={styles.validationError}>{validationError}</div>}
        {resendText}
        {errorText}
        {withConfirmButton &&
          (stickyConfirmButton ? (
            <FullScreenFooter contentRef={contentRef}>{confirmButton}</FullScreenFooter>
          ) : (
            <Box mt='x16' fullWidth>
              {confirmButton}
            </Box>
          ))}
        {isLoginAuthEvent && (
          <Box mt='x16'>
            <Button.Ghost size='md' onClick={openRecoveryModal}>
              {t('Sign up.Enter recovery code')}
            </Button.Ghost>
          </Box>
        )}
      </Box>
    </Form>
  )
}

interface OuterProps {
  onSubmit(code: string): Promise<void>
  openRecoveryModal: () => void
  statusType: 'success' | 'error' | undefined
  validationError?: string
  resendText: JSX.Element
  errorText: JSX.Element
  autoFocus?: boolean
  withConfirmButton?: boolean
  stickyConfirmButton?: boolean
  contentRef?: RefObject<HTMLDivElement | null>
}

export const TwoFactorAuthForm = withFormik<OuterProps, CodeFormValues>({
  mapPropsToValues: () => {
    return {
      code1: '',
      code2: '',
      code3: '',
      code4: '',
      code5: '',
      code6: '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting, setFieldValue }) => {
    try {
      const code =
        values.code1 + values.code2 + values.code3 + values.code4 + values.code5 + values.code6
      await props.onSubmit(code)
    } catch {
      fieldKeys.forEach((fk) => setFieldValue(fk, ''))
      document.getElementById(fieldKeys[0])?.focus()
    } finally {
      setSubmitting(false)
    }
  },
  enableReinitialize: true,
})(TwoFactorAuthFormUI)
